/**
 * Default node templates for the process builder.
 * These templates define the initial configuration for each node type.
 */

export const nodeTemplates = {
  // LLM Node Template
  llmNode: {
    name: 'New LLM Node',
    description: '',
    execution_type: 'llm',
    prompt_template: 'You are a helpful assistant.\n\nInput: {{input}}\n\nPlease process the input and provide a response.',
    system_prompt: 'You are a helpful AI assistant.',
    model: 'gpt-4o',
    temperature: 0.3,
    max_tokens: 2048,
    inputs: [
      {
        name: 'input',
        type: 'string',
        required: true,
        default: '',
        description: 'Main input for the LLM',
      },
    ],
    outputs: [
      {
        name: 'response',
        type: 'string',
        description: 'LLM response',
        extract_json_key: null,
      },
    ],
    timeout_seconds: 120,
    retry_count: 2,
    expect_json: false,
  },

  // Platform Pipeline Node Template
  snnPipelineNode: {
    name: 'New Platform Pipeline Node',
    description: '',
    execution_type: 'snn_pipeline',
    prompt_template: '',
    system_prompt: '',
    model: 'gpt-4o',
    temperature: 0.3,
    max_tokens: 2048,
    snn_config: {
      execution_mode: 'BUILD_AND_RUN',
      snn_execution_mode: 'not_compiled',
      max_nodes: 256,
      max_hops: 3,
      similarity_top_k: 10,
      decompose_query: true,
      nl_query_input: 'nl_query',
    },
    inputs: [
      {
        name: 'nl_query',
        type: 'string',
        required: true,
        default: '',
        description: 'Natural language query for the Platform pipeline',
      },
    ],
    outputs: [
      {
        name: 'snn_response',
        type: 'string',
        description: 'Platform pipeline response',
      },
    ],
    timeout_seconds: 120,
    retry_count: 2,
    expect_json: true,
  },

  // Gate Node Template
  gateNode: {
    name: 'New Gate',
    description: '',
    gate_type: 'AND',
    input_nodes: [],
    output_nodes: [],
    threshold: null,
  },

  // Case Evidence Extractor Node Template (categorical deep-chain reasoner)
  caseExtractorNode: {
    name: 'Case Evidence Extractor',
    description: 'LLM-driven extractor that turns raw case text into a structured CaseEvidenceFrame matching case_evidence_schema.json. Generic across every case in every domain.',
    execution_type: 'case_extractor',
    prompt_template: '',
    system_prompt: '',
    model: 'gpt-4o',
    temperature: 0.0,
    max_tokens: 12000,
    case_extractor_config: {
      schema_path: 'GraphBuilder/backend/app/process_engine/case_evidence_schema.json',
      case_text_input: 'case_text',
      case_id_input: 'case_id',
      domain_input: 'domain',
      extra_inputs: [],
      llm_model: 'gpt-4o',
      temperature: 0.0,
      max_tokens: 12000,
      timeout_seconds: 480,
      cache_by_hash: true,
    },
    inputs: [
      {
        name: 'case_text',
        type: 'long_string',
        required: true,
        default: '',
        description: 'Raw case narrative (paste or upload).',
      },
      {
        name: 'case_id',
        type: 'string',
        required: false,
        default: '',
        description: 'Optional stable id for this case.',
      },
      {
        name: 'domain',
        type: 'domain',
        required: false,
        default: 'AML',
        description: 'Top-level domain. Drives which packs the registry selects.',
      },
    ],
    outputs: [
      { name: 'case_evidence_frame', type: 'json', description: 'Structured CaseEvidenceFrame.' },
      { name: 'case_id', type: 'string' },
      { name: 'domain', type: 'string' },
      { name: 'n_entities', type: 'number' },
      { name: 'n_transactions', type: 'number' },
      { name: 'extractor_warnings', type: 'json' },
    ],
    timeout_seconds: 480,
    retry_count: 1,
    expect_json: true,
  },

  // HTTP Node Template -- calls an external HTTP API with Jinja2-templated
  // URL / headers / body. Auth credentials live in WorkspaceSecret rows.
  httpNode: {
    name: 'New HTTP Node',
    description: '',
    execution_type: 'http',
    prompt_template: '',
    system_prompt: '',
    model: '',
    temperature: 0.0,
    max_tokens: 0,
    http_config: {
      method: 'GET',
      url: 'https://api.example.com/resource/{{ resource_id }}',
      headers: {},
      query_params: {},
      body_mode: 'none',
      body_json: null,
      body_raw: null,
      body_form: null,
      auth_type: 'none',
      auth_credential_ref: null,
      auth_param_name: null,
      expected_status_codes: [200, 201, 202, 204],
      response_mode: 'json',
      follow_redirects: true,
      verify_tls: true,
    },
    inputs: [
      {
        name: 'resource_id',
        type: 'string',
        required: true,
        default: '',
        description: 'Example template variable. Rename or remove as needed.',
      },
    ],
    outputs: [
      {
        name: 'response',
        type: 'json',
        description: 'Full parsed response body (JSON or text).',
      },
    ],
    timeout_seconds: 30,
    retry_count: 0,
    expect_json: true,
  },

  // Python Node Template -- runs user code in a sandboxed subprocess.
  // The bundled cm_mcp helper lets the code call MCP servers from the
  // workspace registry.
  pythonNode: {
    name: 'New Python Node',
    description: '',
    execution_type: 'python',
    prompt_template: '',
    system_prompt: '',
    model: '',
    temperature: 0.0,
    max_tokens: 0,
    python_config: {
      code: [
        'def main(inputs):',
        '    """Entrypoint called by the Python node sandbox.',
        '',
        '    Args:',
        '        inputs: dict of declared input names -> values.',
        '    Returns:',
        '        dict whose keys match declared output names.',
        '    """',
        '    name = inputs.get("name", "world")',
        '    return {"greeting": f"hello, {name}"}',
        '',
      ].join('\n'),
      requirements: [],
      entrypoint: 'main',
      cpu_seconds: 30,
      wall_seconds: 60,
      memory_mb: 512,
      network_access: false,
      mcp_servers: [],
    },
    inputs: [
      {
        name: 'name',
        type: 'string',
        required: false,
        default: 'world',
        description: 'Example input. Rename or remove as needed.',
      },
    ],
    outputs: [
      {
        name: 'greeting',
        type: 'string',
        description: 'Greeting returned by the sandboxed code.',
      },
    ],
    timeout_seconds: 90,
    retry_count: 0,
    expect_json: true,
  },

  // Cross-Link Node Template -- invokes another process map, graph, or router
  // and captures its result. Enables interconnected networks of process maps.
  crossLinkNode: {
    name: 'New Cross-Link Node',
    description: 'Invoke another process map (or a graph/router) and capture its output.',
    execution_type: 'cross_link',
    prompt_template: '',
    system_prompt: '',
    model: '',
    temperature: 0.0,
    max_tokens: 0,
    cross_link_config: {
      target_type: 'process',
      target_process_id: null,
      target_process_name: null,
      workspace_id: null,
      graph_id: null,
      graph_name: null,
      router_graph_id: null,
      router_graph_name: null,
      nl_query_input: 'nl_query',
      input_mapping: null,
      output_mapping: null,
      output_prefix: null,
      propagate_workspace: true,
      auto_resolve_ambiguous: true,
    },
    inputs: [
      {
        name: 'input',
        type: 'string',
        required: false,
        default: '',
        description: 'Passed to the invoked target. Rename/remove as needed.',
      },
    ],
    outputs: [
      {
        name: 'result',
        type: 'json',
        description: 'Output captured from the invoked target.',
      },
    ],
    timeout_seconds: 300,
    retry_count: 0,
    expect_json: true,
  },

  // Categorical Chain Reasoner Node Template (multi-pack mode)
  chainReasonerNode: {
    name: 'Categorical Deep-Chain Reasoner',
    description: 'Loads every category pack from the registry, evaluates each pack against the CaseEvidenceFrame, and emits a ViolationsReport. The optional decision_policy.yaml computes a binding aggregate decision.',
    execution_type: 'chain_reasoner',
    prompt_template: '',
    system_prompt: '',
    model: 'chain_reasoner_multi_pack',
    temperature: 0.0,
    max_tokens: 1,
    chain_reasoner_config: {
      mode: 'multi_pack',
      pack_path: '',
      pack_registry_path: 'poc_snn_graph_nolm/reference_corpus/pack_registry.yaml',
      manifest_path: 'poc_snn_graph_nolm/reference_corpus/manifest.json',
      decision_policy_path: 'poc_snn_graph_nolm/reference_corpus/decision_policy.yaml',
      resolver: 'snn_pipeline',
      pipeline_run_url: 'http://127.0.0.1:8000/api/pipeline/run',
      case_evidence_input: 'case_evidence_frame',
      case_text_input: 'case_text',
      domain_filter: [],
      max_iterations_per_pack: 200,
      require_at_least_one_case_predicate_per_chain: true,
      near_coverage_delta: 1,
      timeout_seconds: 720,
    },
    inputs: [
      { name: 'case_evidence_frame', type: 'json', required: true, default: null, description: 'Structured frame from the case extractor.' },
      { name: 'case_text', type: 'string', required: false, default: '', description: 'Optional raw narrative for case_nl predicates.' },
    ],
    outputs: [
      { name: 'violations_report', type: 'json' },
      { name: 'decision', type: 'string' },
      { name: 'aggregate_decision', type: 'string' },
      { name: 'aggregate_score', type: 'number' },
      { name: 'score', type: 'number' },
      { name: 'fired_pack_ids', type: 'json' },
      { name: 'total_packs_fired', type: 'number' },
      { name: 'total_packs_loaded', type: 'number' },
      { name: 'total_instances_fired', type: 'number' },
      { name: 'evidence_summary', type: 'string' },
      { name: 'case_evidence_frame', type: 'json' },
    ],
    timeout_seconds: 900,
    retry_count: 1,
    expect_json: true,
  },
};

/**
 * Get a fresh copy of a node template with a unique node_id
 */
export function getNodeTemplate(type) {
  const template = nodeTemplates[type];
  if (!template) {
    throw new Error(`Unknown node type: ${type}`);
  }

  return {
    ...JSON.parse(JSON.stringify(template)),
    node_id: `${type}_${Date.now()}`,
  };
}

/**
 * Available models for LLM nodes
 */
export const availableModels = [
  { value: 'gpt-4o', label: 'GPT-4o' },
  { value: 'gpt-4o-mini', label: 'GPT-4o Mini' },
  { value: 'gpt-4-turbo', label: 'GPT-4 Turbo' },
  { value: 'gpt-3.5-turbo', label: 'GPT-3.5 Turbo' },
  { value: 'claude-3-opus', label: 'Claude 3 Opus' },
  { value: 'claude-3-sonnet', label: 'Claude 3 Sonnet' },
];

/**
 * Available input/output types
 */
export const dataTypes = [
  { value: 'string', label: 'String' },
  { value: 'long_string', label: 'Long String (textarea)' },
  { value: 'number', label: 'Number' },
  { value: 'boolean', label: 'Boolean' },
  { value: 'json', label: 'JSON' },
  { value: 'domain', label: 'Domain (AML/KYC/...)' },
  { value: 'array', label: 'Array' },
];

/**
 * Top-level domains used by the categorical chain reasoner. Drives the
 * <select> in ExecutionRunForm when an entry input has type=`domain` or
 * name=`domain`.
 */
export const aamlDomains = [
  { value: 'AML', label: 'AML' },
  { value: 'KYC', label: 'KYC' },
  { value: 'TransactionMonitoring', label: 'Transaction Monitoring' },
  { value: 'Sanctions', label: 'Sanctions' },
  { value: 'FraudPrevention', label: 'Fraud Prevention' },
  { value: 'Other', label: 'Other' },
];

/**
 * Resolvers for the categorical chain_reasoner node.
 *  - keyword: deterministic keyword scoring against source-doc text. Cheap and
 *    works for KGs that haven't been compiled yet.
 *  - snn_pipeline: production-grade NL retrieval against compiled KGs.
 */
export const chainReasonerResolvers = [
  { value: 'snn_pipeline', label: 'Platform pipeline (production)' },
  { value: 'keyword', label: 'Keyword (offline / debugging)' },
];

/**
 * Modes exposed on the chain_reasoner node properties panel.
 */
export const chainReasonerModes = [
  { value: 'multi_pack', label: 'Multi-pack categorical (recommended)' },
  { value: 'single_pack', label: 'Single pack (legacy)' },
];

/**
 * Severity badge colour mapping used by ViolationsReportView.
 */
export const severityColours = {
  CRITICAL: '#c0392b',
  HIGH: '#c0392b',
  MEDIUM: '#e67e22',
  LOW: '#27ae60',
  UNKNOWN: '#7f8c8d',
};

/**
 * Cross-link target types. Selects what a cross_link node invokes.
 */
export const crossLinkTargetTypes = [
  { value: 'process', label: 'Process Map (invoke another process)' },
  { value: 'graph', label: 'Graph (direct Platform pipeline query)' },
  { value: 'router', label: 'Router (route to a graph at runtime)' },
];

/**
 * Gate types
 */
export const gateTypes = [
  { value: 'AND', label: 'AND - All inputs required' },
  { value: 'OR', label: 'OR - Any input triggers' },
  { value: 'THRESHOLD', label: 'THRESHOLD - N of M inputs' },
  { value: 'CONFIDENCE', label: 'CONFIDENCE - Score-based routing' },
];

/**
 * Platform Execution modes
 */
export const snnExecutionModes = [
  { value: 'BUILD_AND_RUN', label: 'Build & Run' },
  { value: 'BUILD_ONLY', label: 'Build Only' },
];

export const snnCompilationModes = [
  { value: 'not_compiled', label: 'Not Compiled (Debug)' },
  { value: 'compiled_full', label: 'Compiled Full' },
  { value: 'compiled_full_numpy', label: 'Compiled Full - Fast Engine' },
  { value: 'compiled_sharded', label: 'Compiled Sharded (Legacy)' },
];

/**
 * HTTP node enumerations -- kept in sync with the Pydantic enums in
 * GraphBuilder/backend/app/process_engine/models.py so the dropdowns
 * exactly match what the backend accepts.
 */
export const httpMethods = [
  { value: 'GET', label: 'GET' },
  { value: 'POST', label: 'POST' },
  { value: 'PUT', label: 'PUT' },
  { value: 'PATCH', label: 'PATCH' },
  { value: 'DELETE', label: 'DELETE' },
  { value: 'HEAD', label: 'HEAD' },
  { value: 'OPTIONS', label: 'OPTIONS' },
];

export const httpAuthTypes = [
  { value: 'none', label: 'None' },
  { value: 'basic', label: 'Basic (user:password secret)' },
  { value: 'bearer', label: 'Bearer Token' },
  { value: 'header', label: 'Custom Header' },
  { value: 'api_key_query', label: 'API Key (query parameter)' },
];

export const httpBodyModes = [
  { value: 'none', label: 'None' },
  { value: 'json', label: 'JSON' },
  { value: 'raw', label: 'Raw text' },
  { value: 'form', label: 'Form (x-www-form-urlencoded)' },
];

export const httpResponseModes = [
  { value: 'json', label: 'Parse as JSON' },
  { value: 'text', label: 'Plain text' },
  { value: 'status_only', label: 'Status code only' },
];

export default nodeTemplates;
